/**
 * BulkScoreUpload Component
 *
 * File upload component for importing training scores from JSON
 * Task: F.4 - Bulk Upload Component
 *
 * Features:
 * - JSON file selection
 * - Pre-upload validation
 * - Preview of records
 * - Upload progress
 * - Result summary
 */

import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  LinearProgress,
  Alert,
  Collapse,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import PreviewIcon from '@mui/icons-material/Preview';
import { validateImportData, getImportTemplate, downloadJson } from '../../api/trainingApi';

/**
 * BulkScoreUpload Component
 *
 * @param {Object} props
 * @param {Function} props.onUpload - Async callback: (parsedData) => Promise<ImportResult>
 * @param {boolean} props.isUploading - External uploading state
 * @param {Function} props.onUploadComplete - Callback after successful upload
 */
const BulkScoreUpload = ({ onUpload, isUploading = false, onUploadComplete }) => {
  const fileInputRef = useRef(null);

  // State
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle file selection
  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset state
    setError(null);
    setValidationResult(null);
    setUploadResult(null);
    setParsedData(null);
    setShowPreview(false);

    // Validate file type
    if (!file.name.endsWith('.json')) {
      setError('Please select a JSON file (.json)');
      return;
    }

    // Read and parse file
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const json = JSON.parse(e.target.result);
        setParsedData(json);
        setSelectedFile(file);

        // Validate structure
        const validation = validateImportData(json);
        setValidationResult(validation);

        if (validation.valid) {
          setShowPreview(true);
        }
      } catch (parseError) {
        setError(`Invalid JSON: ${parseError.message}`);
        setSelectedFile(null);
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  // Handle upload
  const handleUpload = async () => {
    if (!parsedData || !validationResult?.valid || !onUpload) return;

    setError(null);
    setUploadResult(null);
    setUploadProgress(10);

    try {
      // Simulate progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => Math.min(prev + 10, 90));
      }, 200);

      const result = await onUpload(parsedData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (result.success) {
        setUploadResult(result);
        if (onUploadComplete) {
          onUploadComplete(result);
        }
      } else {
        setError(result.error || 'Upload failed');
      }
    } catch (uploadError) {
      setError(uploadError.message || 'Upload failed');
    } finally {
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  // Handle clear/reset
  const handleClear = () => {
    setSelectedFile(null);
    setParsedData(null);
    setValidationResult(null);
    setUploadResult(null);
    setError(null);
    setShowPreview(false);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Download template
  const handleDownloadTemplate = () => {
    const template = getImportTemplate();
    downloadJson(template, 'training_scores_template.json');
  };

  // Get score summary for preview
  const getScoreSummary = () => {
    if (!parsedData?.scores) return null;

    const summary = {
      total: parsedData.scores.length,
      excellent: 0,
      good: 0,
      neutral: 0,
      needsImprovement: 0,
      bad: 0
    };

    parsedData.scores.forEach((score) => {
      switch (score.scoreOverall) {
        case 2: summary.excellent++; break;
        case 1: summary.good++; break;
        case 0: summary.neutral++; break;
        case -1: summary.needsImprovement++; break;
        case -2: summary.bad++; break;
        default: break;
      }
    });

    return summary;
  };

  const scoreSummary = getScoreSummary();

  return (
    <Paper
      sx={{
        backgroundColor: '#292b2f',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        p: 3
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>
          Bulk Score Import
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          onClick={handleDownloadTemplate}
          sx={{ color: 'rgba(255,255,255,0.7)' }}
        >
          Download Template
        </Button>
      </Box>

      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 3 }}>
        Upload a JSON file containing reviewed scores from Claude. The file should follow the template structure.
      </Typography>

      {/* Upload Progress */}
      {(isUploading || uploadProgress > 0) && (
        <Box sx={{ mb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={uploadProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.1)',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#7c3aed',
                borderRadius: 3
              }
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', mt: 0.5, display: 'block' }}>
            Uploading... {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Error Alert */}
      <Collapse in={!!error}>
        <Alert
          severity="error"
          sx={{ mb: 2, backgroundColor: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      </Collapse>

      {/* Validation Errors */}
      <Collapse in={validationResult && !validationResult.valid}>
        <Alert
          severity="warning"
          sx={{ mb: 2, backgroundColor: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Validation Failed:
          </Typography>
          <List dense disablePadding>
            {validationResult?.errors?.map((err, idx) => (
              <ListItem key={idx} disablePadding sx={{ py: 0.25 }}>
                <ListItemIcon sx={{ minWidth: 24 }}>
                  <WarningIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                </ListItemIcon>
                <ListItemText
                  primary={err}
                  primaryTypographyProps={{ variant: 'caption' }}
                />
              </ListItem>
            ))}
          </List>
        </Alert>
      </Collapse>

      {/* Success Result */}
      <Collapse in={!!uploadResult}>
        <Alert
          severity="success"
          sx={{ mb: 2, backgroundColor: 'rgba(34, 197, 94, 0.1)', color: '#22c55e' }}
          onClose={handleClear}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Import Successful!
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Chip label={`${uploadResult?.imported || 0} Imported`} size="small" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }} />
            <Chip label={`${uploadResult?.skipped || 0} Skipped`} size="small" sx={{ backgroundColor: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' }} />
            {uploadResult?.errors?.length > 0 && (
              <Chip label={`${uploadResult.errors.length} Errors`} size="small" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }} />
            )}
          </Box>
        </Alert>
      </Collapse>

      {/* File Drop Zone */}
      {!selectedFile && !uploadResult && (
        <Box
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed rgba(124, 58, 237, 0.3)',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: '#7c3aed',
              backgroundColor: 'rgba(124, 58, 237, 0.05)'
            }
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 48, color: '#7c3aed', mb: 1 }} />
          <Typography variant="body1" sx={{ color: 'white', mb: 0.5 }}>
            Click to select or drag & drop
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Accepted format: JSON (.json)
          </Typography>
        </Box>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />

      {/* Selected File Info */}
      {selectedFile && !uploadResult && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 2,
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 1,
            mb: 2
          }}
        >
          <InsertDriveFileIcon sx={{ color: '#7c3aed' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
              {selectedFile.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              {(selectedFile.size / 1024).toFixed(1)} KB
              {parsedData?.scores && ` • ${parsedData.scores.length} scores`}
            </Typography>
          </Box>
          {validationResult?.valid && (
            <CheckCircleIcon sx={{ color: '#22c55e' }} />
          )}
          {validationResult && !validationResult.valid && (
            <ErrorIcon sx={{ color: '#ef4444' }} />
          )}
          <IconButton size="small" onClick={handleClear} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Preview Section */}
      <Collapse in={showPreview && validationResult?.valid}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PreviewIcon sx={{ color: '#7c3aed', fontSize: 20 }} />
          <Typography variant="subtitle2" sx={{ color: 'white' }}>
            Preview
          </Typography>
        </Box>

        {/* Metadata */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Chip
            label={`Reviewed by: ${parsedData?.reviewedBy || 'Unknown'}`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
          />
          <Chip
            label={`Review date: ${parsedData?.reviewDate || 'N/A'}`}
            size="small"
            sx={{ backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' }}
          />
        </Box>

        {/* Score Summary */}
        {scoreSummary && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip label={`Total: ${scoreSummary.total}`} size="small" variant="outlined" sx={{ borderColor: 'rgba(255,255,255,0.3)', color: 'white' }} />
            {scoreSummary.excellent > 0 && <Chip label={`Excellent: ${scoreSummary.excellent}`} size="small" sx={{ backgroundColor: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' }} />}
            {scoreSummary.good > 0 && <Chip label={`Good: ${scoreSummary.good}`} size="small" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' }} />}
            {scoreSummary.neutral > 0 && <Chip label={`Neutral: ${scoreSummary.neutral}`} size="small" sx={{ backgroundColor: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' }} />}
            {scoreSummary.needsImprovement > 0 && <Chip label={`Needs Work: ${scoreSummary.needsImprovement}`} size="small" sx={{ backgroundColor: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b' }} />}
            {scoreSummary.bad > 0 && <Chip label={`Bad: ${scoreSummary.bad}`} size="small" sx={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' }} />}
          </Box>
        )}
      </Collapse>

      {/* Action Buttons */}
      {selectedFile && !uploadResult && (
        <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              borderColor: 'rgba(255,255,255,0.2)',
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { borderColor: 'rgba(255,255,255,0.3)' }
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!validationResult?.valid || isUploading}
            startIcon={<CloudUploadIcon />}
            sx={{
              backgroundColor: '#7c3aed',
              '&:hover': { backgroundColor: '#6d28d9' },
              '&.Mui-disabled': { backgroundColor: 'rgba(124, 58, 237, 0.3)' }
            }}
          >
            {isUploading ? 'Uploading...' : 'Upload Scores'}
          </Button>
        </Box>
      )}

      {/* Upload Another */}
      {uploadResult && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClear}
            sx={{
              borderColor: '#7c3aed',
              color: '#7c3aed',
              '&:hover': { borderColor: '#6d28d9', backgroundColor: 'rgba(124, 58, 237, 0.1)' }
            }}
          >
            Upload Another File
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export default BulkScoreUpload;
